// Phase 11 — Bridge card: legacy UI shell, new proposal data.
//
// The same card is rendered by both the (rewired) Action Queue and the still-
// legacy Action History page, so its prop type accepts either flavor:
//   * Legacy `ActionProposal` (seller-JWT V2 endpoint) — kept for history.
//   * New `Proposal` (Phase 8-F employee-scoped endpoint) — used by the queue.
//
// New fields rendered (when present on the proposal):
//   * `execution_type` badge — gray for legacy/platform_api/propose, blue for
//     official_mcp. Lets reviewers see at a glance how the side-effect will
//     actually be applied.
//   * `requested_by_employee_id` — surfaced in the footer meta line so it's
//     visible without clicking through to detail view.
//   * `auto_approval_status` / `auto_approved` — already shown as a tag.
//
// Action buttons are wrapped in RoleGate so analysts and read-only roles
// don't see Approve/Deny at all. Confirm dialogs handled by the parent.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionProposal } from '../../../lib/api/actions/action-types';
import { Proposal } from '../../../lib/api/proposals/proposals-api';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { RoleGate } from '../../../lib/rbac';
import { proposalRiskLabel, proposalStatusLabel } from './proposalBadges';
import {
  FailureReason,
  autoApprovalRuleName,
  isAutoApproved,
  isCurrentValueUnknown,
  proposalEntity,
  proposalFailure,
  UnknownCurrentValue,
} from './proposalFormat';
import '../actions.scss';

// Permissive union — the card only reads optional fields from each shape, so
// we accept either and let downstream code defensively fall back. Using a
// union (rather than intersection) keeps callers honest about which fields
// they own, and matches the runtime reality that the queue and history pages
// hand us different shapes.
type AnyProposal = ActionProposal | Proposal;

interface Props {
  proposal: AnyProposal;
  onApprove?: (id: string, notes?: string) => void;
  onDeny?: (id: string, reason?: string) => void;
  showActions: boolean;
}

const ActionProposalCard = ({
  proposal,
  onApprove,
  onDeny,
  showActions,
}: Props) => {
  const { t } = useTranslation();
  const [showApproveInput, setShowApproveInput] = useState(false);
  const [showDenyInput, setShowDenyInput] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [denyReason, setDenyReason] = useState('');

  const getPlatformLabel = (platform: string): string => {
    switch (platform) {
      case 'facebook_ads':
      case 'facebook':
        return 'FB';
      case 'google_ads':
        return 'GA';
      case 'tiktok_ads':
      case 'tiktok':
        return 'TT';
      case 'linkedin_ads':
      case 'linkedin':
        return 'LI';
      case 'ga4':
        return 'G4';
      case 'gsc':
        return 'SC';
      default:
        return (platform || '').substring(0, 2).toUpperCase();
    }
  };

  const getPlatformName = (platform: string): string => {
    switch (platform) {
      case 'facebook_ads':
      case 'facebook':
        return 'Facebook Ads';
      case 'google_ads':
        return 'Google Ads';
      case 'tiktok_ads':
      case 'tiktok':
        return 'TikTok Ads';
      case 'linkedin_ads':
      case 'linkedin':
        return 'LinkedIn Ads';
      case 'ga4':
        return 'GA4';
      case 'gsc':
        return 'Google Search Console';
      default:
        return platform;
    }
  };

  const getActionTypeLabel = (
    actionType?: ActionProposal['action_type'] | Proposal['action_type']
  ): string => {
    if (!actionType) {
      // New proposals expose a `tool_name` when there is no action_type linked.
      const toolName = (proposal as Proposal).tool_name;
      if (toolName) {
        return toolName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      }
      return t('Action');
    }
    const name = actionType.name || actionType.action_key || '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatValue = (value: Record<string, any>): string => {
    if (!value) return '-';
    // Try common value fields
    if (value.daily_budget !== undefined) {
      return `$${(Number(value.daily_budget) / 100).toFixed(2)}/day`;
    }
    if (value.lifetime_budget !== undefined) {
      return `$${(Number(value.lifetime_budget) / 100).toFixed(2)} lifetime`;
    }
    if (value.bid_amount !== undefined) {
      return `$${(Number(value.bid_amount) / 100).toFixed(2)} bid`;
    }
    if (value.status !== undefined) {
      return String(value.status);
    }
    // Fallback: show first key-value
    const keys = Object.keys(value);
    if (keys.length === 0) return '-';
    const firstKey = keys[0];
    return `${firstKey}: ${value[firstKey]}`;
  };

  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('JustNow');
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // New proposals carry an `execution_type` indicating how the action is
  // actually carried out. Old proposals don't have this — we surface a
  // generic "legacy" tag when the field is absent so reviewers can tell at
  // a glance which API the worker will hit.
  const executionType: string =
    (proposal as Proposal).execution_type || 'legacy';
  const executionTypeLabel =
    executionType === 'official_mcp' ? 'Official MCP' : executionType;
  const executionTypeVariant =
    executionType === 'official_mcp' ? 'mcp' : 'legacy';

  // Requested-by line. The new endpoint returns an employee UUID; until we
  // resolve to a name, surface the raw id. Old proposals don't carry this.
  const requestedBy = (proposal as Proposal).requested_by_employee_id;

  const handleApprove = () => {
    if (showApproveInput) {
      onApprove?.(proposal.id, approveNotes || undefined);
      setShowApproveInput(false);
      setApproveNotes('');
    } else {
      setShowApproveInput(true);
      setShowDenyInput(false);
    }
  };

  const handleDeny = () => {
    if (showDenyInput) {
      onDeny?.(proposal.id, denyReason || undefined);
      setShowDenyInput(false);
      setDenyReason('');
    } else {
      setShowDenyInput(true);
      setShowApproveInput(false);
    }
  };

  const renderResultInfo = () => {
    if (proposal.status === 'completed' && proposal.execution_result) {
      return (
        <div className='action-proposal-card__result action-proposal-card__result--success'>
          <span className='action-proposal-card__result-label'>
            {t('Result')}:
          </span>
          {typeof proposal.execution_result === 'object'
            ? JSON.stringify(proposal.execution_result)
            : String(proposal.execution_result)}
        </div>
      );
    }

    const failure = proposalFailure(proposal);
    if (failure) {
      return (
        <div className='action-proposal-card__result action-proposal-card__result--error'>
          <span className='action-proposal-card__result-label'>
            {t('ProposalFailureReason')}:
          </span>
          <FailureReason reason={failure.reason} t={t} />
        </div>
      );
    }

    if (proposal.status === 'denied' && proposal.review_notes) {
      return (
        <div className='action-proposal-card__result action-proposal-card__result--denied'>
          <span className='action-proposal-card__result-label'>
            {t('DenialReason')}:
          </span>
          {proposal.review_notes}
        </div>
      );
    }

    return null;
  };

  // Approved by a rule: the flag, or the rule the proposal names.
  const autoApproved = isAutoApproved(proposal);
  const ruleName = autoApprovalRuleName(proposal);
  const entity = proposalEntity(proposal);

  // Brand display: new proposals carry a `shop` relationship; fall back to id.
  const shopLabel =
    (proposal as Proposal).shop?.name ||
    (proposal as Proposal).shop?.slug ||
    `shop #${proposal.shop_id}`;

  return (
    <div className='action-proposal-card'>
      {/* Header */}
      <div className='action-proposal-card__header'>
        <div className='action-proposal-card__header-left'>
          <div className='action-proposal-card__header-platform'>
            {getPlatformLabel(proposal.platform)}
          </div>
          <h4 className='action-proposal-card__header-title'>
            {getActionTypeLabel(proposal.action_type)}
          </h4>
        </div>
        <div className='action-proposal-card__header-badges'>
          <span
            className={`execution-type-badge execution-type-badge--${executionTypeVariant}`}
            title={t('ExecutionType')}
          >
            {executionTypeLabel}
          </span>
          <span className={`risk-badge risk-badge--${proposal.risk_level}`}>
            {proposalRiskLabel(t, proposal.risk_level)}
          </span>
        </div>
      </div>

      {/* Entity info */}
      <p className='action-proposal-card__entity'>
        {entity.primary}
        {entity.secondary && ` (ID: ${entity.secondary})`}
        {' - '}
        {getPlatformName(proposal.platform)}
        {' - '}
        {shopLabel}
      </p>

      <hr className='action-proposal-card__divider' />

      {/* Value change */}
      <div className='value-change'>
        <span className='value-change__label'>{t('Current')}:</span>
        {isCurrentValueUnknown(proposal) ? (
          <UnknownCurrentValue />
        ) : (
          <span className='value-change__current'>
            {formatValue(proposal.current_value)}
          </span>
        )}
        <span className='value-change__arrow'>&rarr;</span>
        <span className='value-change__label'>{t('Proposed')}:</span>
        <span className='value-change__proposed'>
          {formatValue(proposal.proposed_value)}
        </span>
        {proposal.change_percentage != null && (
          <span
            className={`value-change__percentage ${
              proposal.change_percentage > 0
                ? 'value-change__percentage--positive'
                : proposal.change_percentage < 0
                ? 'value-change__percentage--negative'
                : 'value-change__percentage--neutral'
            }`}
          >
            {proposal.change_percentage > 0 ? '+' : ''}
            {proposal.change_percentage.toFixed(1)}%
          </span>
        )}
      </div>

      <hr className='action-proposal-card__divider' />

      {/* Reasoning */}
      <div className='action-proposal-card__reasoning'>
        <p className='action-proposal-card__reasoning-label'>
          {t('Reasoning')}
        </p>
        <p className='action-proposal-card__reasoning-text'>
          {proposal.reasoning}
        </p>
      </div>

      {/* Result info for history view */}
      {renderResultInfo()}

      {/* Footer */}
      <div className='action-proposal-card__footer'>
        <div className='action-proposal-card__footer-meta'>
          <span className={`status-badge status-badge--${proposal.status}`}>
            {proposalStatusLabel(t, proposal.status)}
          </span>
          <span>{getRelativeTime(proposal.created_at)}</span>
          {autoApproved && (
            <span className='platform-tag'>{t('AutoApproved')}</span>
          )}
          {ruleName && (
            <span className='platform-tag'>
              {t('Rule')}: {ruleName}
            </span>
          )}
          {requestedBy && (
            <span className='platform-tag'>
              {t('RequestedBy')}: {requestedBy}
            </span>
          )}
        </div>

        {showActions &&
          (proposal.status === 'pending_review' ||
            proposal.status === 'proposed') && (
            <div className='action-proposal-card__footer-actions'>
              <RoleGate capability='proposals.approve'>
                <MainSquareButton
                  value={showApproveInput ? t('Confirm') : t('Approve')}
                  type='button'
                  buttonType='success'
                  onClick={handleApprove}
                />
              </RoleGate>
              <RoleGate capability='proposals.deny'>
                <MainSquareButton
                  value={showDenyInput ? t('Confirm') : t('Deny')}
                  type='button'
                  buttonType='delete'
                  onClick={handleDeny}
                />
              </RoleGate>
            </div>
          )}
      </div>

      {/* Inline approval notes input */}
      {showApproveInput && (
        <div className='action-proposal-card__inline-input'>
          <input
            type='text'
            placeholder={t('OptionalApprovalNotes')}
            value={approveNotes}
            onChange={(e) => setApproveNotes(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleApprove();
            }}
          />
          <MainSquareButton
            value={t('Cancel')}
            type='button'
            buttonType='white'
            onClick={() => {
              setShowApproveInput(false);
              setApproveNotes('');
            }}
          />
        </div>
      )}

      {/* Inline denial reason input — required, min 5 chars enforced by
          the parent's handleDeny. */}
      {showDenyInput && (
        <div className='action-proposal-card__inline-input'>
          <input
            type='text'
            placeholder={t('DenialReasonRequired')}
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleDeny();
            }}
          />
          <MainSquareButton
            value={t('Cancel')}
            type='button'
            buttonType='white'
            onClick={() => {
              setShowDenyInput(false);
              setDenyReason('');
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ActionProposalCard;
